
import { useState, useEffect } from 'react';
import { getDualStackIPs, hashIPWithDate } from '@/utils/crypto';

interface DualStackHashState {
  ipHash: string | null;       // Primary (IPv4 preferred) — backward-compat
  ipHashAlt: string | null;    // Alternate stack (IPv6 if primary is v4, or v4 if primary is v6)
  realIP: string | null;       // Primary IP string
  realIPAlt: string | null;    // Alternate IP string
  isLoading: boolean;
}

export const useSecureIPHash = () => {
  const [state, setState] = useState<DualStackHashState>({
    ipHash: null,
    ipHashAlt: null,
    realIP: null,
    realIPAlt: null,
    isLoading: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ips = await getDualStackIPs();
        // Prefer IPv4 as primary because game servers usually verify with IPv4
        const primary = ips.ipv4 || ips.primary || ips.ipv6 || '';
        const alt =
          primary === ips.ipv4
            ? (ips.ipv6 || '')
            : primary === ips.ipv6
              ? (ips.ipv4 || '')
              : (ips.ipv6 || ips.ipv4 || '');

        const [primaryHash, altHash] = await Promise.all([
          primary ? hashIPWithDate(primary) : Promise.resolve(''),
          alt ? hashIPWithDate(alt) : Promise.resolve(''),
        ]);

        if (cancelled) return;
        setState({
          ipHash: primaryHash || null,
          ipHashAlt: altHash && altHash !== primaryHash ? altHash : null,
          realIP: primary || null,
          realIPAlt: alt && alt !== primary ? alt : null,
          isLoading: false,
        });
      } catch (e) {
        console.error('Failed to generate dual-stack IP hashes:', e);
        // Fallback: browser fingerprint hash so the user can still vote
        try {
          const fp =
            (navigator.userAgent || 'ua') + '|' + new Date().toDateString();
          const enc = new TextEncoder();
          const buf = await crypto.subtle.digest('SHA-256', enc.encode(fp));
          const h = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
          if (cancelled) return;
          setState({
            ipHash: h,
            ipHashAlt: null,
            realIP: null,
            realIPAlt: null,
            isLoading: false,
          });
        } catch {
          if (cancelled) return;
          setState((s) => ({ ...s, isLoading: false }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
